// HuggingFace-specific utility functions for scope-based data organization

/**
 * Group HuggingFace models by scope (read, write, private)
 * @param {Array} models - Array of model objects with read, write, private properties
 * @returns {Object} Grouped models by scope
 */
export function groupHuggingFaceByScope(models) {
  const scopeGroups = {
    read: { true: [], false: [] },
    write: { true: [], false: [] },
    private: { true: [], false: [] }
  };

  models.forEach(model => {
    // Group by read permission
    if (model.read === true) {
      scopeGroups.read.true.push(model);
    } else if (model.read === false) {
      scopeGroups.read.false.push(model);
    }

    // Group by write permission
    if (model.write === true) {
      scopeGroups.write.true.push(model);
    } else if (model.write === false) {
      scopeGroups.write.false.push(model);
    }

    // Group by private status
    if (model.private === true) {
      scopeGroups.private.true.push(model);
    } else if (model.private === false) {
      scopeGroups.private.false.push(model);
    }
  });

  return scopeGroups;
}

/**
 * Get scope statistics for HuggingFace models
 * @param {Array} models - Array of model objects
 * @returns {Object} Statistics for each scope
 */
export function getHuggingFaceScopeStats(models) {
  const stats = {
    read: { true: 0, false: 0 },
    write: { true: 0, false: 0 },
    private: { true: 0, false: 0 }
  };

  models.forEach(model => {
    if (model.read === true) stats.read.true++;
    else if (model.read === false) stats.read.false++;

    if (model.write === true) stats.write.true++;
    else if (model.write === false) stats.write.false++;

    if (model.private === true) stats.private.true++;
    else if (model.private === false) stats.private.false++;
  });

  return stats;
}

/**
 * Create model list items for display
 * @param {Array} models - Array of model objects
 * @returns {Array} Array of items with name and url properties
 */
export function createHuggingFaceModelItems(models) {
  return models.map(model => ({
    name: model.name,
    url: `https://huggingface.co/${model.name}`
  }));
}

/**
 * Get scope display labels
 * @param {string} scope - The scope name (read, write, private)
 * @param {boolean} value - The boolean value (true/false)
 * @returns {string} Display label
 */
export function getScopeDisplayLabel(scope, value) {
  const labels = {
    read: { true: 'Read Access', false: 'No Read Access' },
    write: { true: 'Write Access', false: 'No Write Access' },
    private: { true: 'Private Models', false: 'Public Models' }
  };
  
  return labels[scope]?.[value] || `${scope}: ${value}`;
}

/**
 * Get scope color scheme
 * @param {string} scope - The scope name (read, write, private)
 * @param {boolean} value - The boolean value (true/false)
 * @returns {string} Color code
 */
export function getScopeColor(scope, value) {
  const colors = {
    read: { true: '#10b981', false: '#ef4444' }, // green for true, red for false
    write: { true: '#3b82f6', false: '#f59e0b' }, // blue for true, amber for false
    private: { true: '#8b5cf6', false: '#06b6d4' } // purple for true, cyan for false
  };
  
  return colors[scope]?.[value] || '#6b7280';
}

